import { useEffect, useRef, useState, useMemo } from "react";
import ItemImage from "./imgs/ItemImage";
import BannerImage from "./imgs/BannerImage";
import { useAppState } from "./Context";
import { useInViewClass } from "../helper/util";
import { STATE } from "../classes/Enum";
import { useSearch } from "./Context";
import HeaderComponent from "./Header";
import ProductOfTheDay from "./ProductOfTheDay";
import RecentlyViewedCarousel from "./RecentlyViewedCarousel";

function makeRand(seed) {
  // simple deterministic-ish hash from string -> 0..1
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967296;
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function buildTransform(seedKey) {
  const r1 = makeRand(seedKey + "|a");
  const r2 = makeRand(seedKey + "|b");
  const r3 = makeRand(seedKey + "|c");

  // keep it subtle (premium): small rotations only
  const rotX = lerp(-10, 10, r1);
  const rotY = lerp(-14, 14, r2);
  const rotZ = lerp(-3, 3, r3);

  return `perspective(900px) rotateX(${rotX}deg) rotateY(${rotY}deg) rotateZ(${rotZ}deg)`;
}

const Banner = ({ title, description, product = null, position="right", query='', state=undefined }) => {
  const { ref, inView } = useInViewClass({ threshold:0.99,   rootMargin: "-25% 0px -20% 0px", });
  const { setState } = useAppState();
  const { setQuery } = useSearch();

  function action() {
    if (query) {
    setState(STATE.CARD);
    setQuery(query);
    }
    if (state) {
      setState(state);
    }
  }

  return (
    <div className={`${inView ? "is-inview": ""}`}
    onClick={() => action()}
    >
        <div className={`banner`}
            style={{ flexDirection: position === "left" ? "row-reverse" : "row" }}
        >
          <div className={`text ${position}`} ref={ref}>
              <h1>{title}</h1>
              <p>{description}</p>
          </div>
            {product ? <BannerImage product={product} position={position}/> : null}
        </div>
        <div className="section-divider"></div>

    </div>

  );
};

const CarouselBanner = ({setSelectedItem, items = [], title, description, position="right"}) => {
  const { setState } = useAppState();
  const { setQuery } = useSearch();
  return (
    <div className=""
    >
        <div className={`banner`}
            style={{ flexDirection: position === "left" ? "row-reverse" : "row" }}
             onClick={() => {setState(STATE.CARD); setQuery('recent')}}
        >
          <div className={` text ${position}`}>
              <h1>{title}</h1>
              <p>{description}</p>
          </div>
          <div className="carousel-banner">
            <Carousel
              items={[...items].sort((a, b) =>
                new Date(b.updatedAt) - new Date(a.updatedAt)
              )}
              setSelectedItem={setSelectedItem}
              variation="banner"
            />
          </div>
        </div>
        <div className="section-divider"></div>
    </div>

  );
}

export const Carousel = ({ items = [], setSelectedItem }) => {
  const { state } = useAppState();
  const MAX_NO = Math.round(window.innerWidth/200) + 5;
  const visibleItems = items.slice(0, MAX_NO);

  // map: key -> transform string (stable across rerenders)
  const transformMap = useMemo(() => {
    const map = new Map();
    for (const item of visibleItems) {
      const key = String(item.id ?? item.setId ?? item.name ?? "item");
      map.set(key, buildTransform(key));
    }
    return map;
  }, [visibleItems]);

  return (
    <div className="carousel">
      <div>
        <div className="carousel-track">
          {[...visibleItems, ...visibleItems].map((item, i) => {
            const k = String(item.id ?? item.setId ?? item.name ?? "item");
            const transform = transformMap.get(k);

            return (
              <div
                key={`${k}-${i}`}
                className="carousel-tilt"
                style={{ transform }}
              >
                <ItemImage
                  item={item}
                  setSelectedItem={setSelectedItem ? setSelectedItem : () => {}}
                  state={state}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ height: "10px" }} />
    </div>
  );
};

const Overview = ({ products = [], setSelectedItem, recentProducts = [] }) => {
  const [picks, setPicks] = useState([null, null, null]);
  const didPickRef = useRef(false);

  useEffect(() => {
    // only pick once, but wait until we actually have products
    if (didPickRef.current) return;

    const cards = (products || []).filter(p => p?.type === "CARD");
    if (cards.length === 0) return; // keep waiting for first real load

    const shuffled = [...cards].sort(() => Math.random() - 0.5);

    setPicks([
      shuffled[0] ?? null,
      shuffled[1] ?? shuffled[0] ?? null,
      shuffled[2] ?? shuffled[0] ?? null,
    ]);

    didPickRef.current = true;
  }, [products]);

  return (
    <div className="overview hide-scrollbar">
      <div className="padding-top"></div>

      {/* <Carousel items={products} setSelectedItem={setSelectedItem}/> */}
      <HeaderComponent/>
      <RecentlyViewedCarousel
        items={recentProducts}
        onSelect={setSelectedItem}
        variant="home"
        title="RECENTLY VIEWED"
        subtitle="Pick up where you left off"
      />
      <ProductOfTheDay products={products} setSelectedItem={setSelectedItem} />
      <CarouselBanner title={"JUST ARRIVED"} description={"Latest and greatest"} items={products} position="right" setSelectedItem={setSelectedItem}/>
      <Banner title="CURATED PRODUCTS" description="Recommended for you" product={picks[0]} position="left" query="popular"/>
      <Banner title="POPULAR CARDS" description="Whats everyone looking at?" product={picks[1]} position="right" query="popular"/>
      <Banner title="MY WISHLIST" description="Click on the heart button to add to your wishlist!" product={picks[2]} position="left" state={STATE.WISHLIST}/>
      <div className="padding-bottom"></div>
    </div>
  );
};

export default Overview;
